import React, { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Check, Loader2, ExternalLink, AlertCircle } from "lucide-react";

/**
 * Card shown on Sitter Dashboard to onboard/manage Stripe Connect payouts.
 */
export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/stripe/connect/status");
      setStatus(data);
    } catch (e) {
      // 400 when profile not yet complete — that's fine
      setStatus({ connected: false });
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/stripe/connect/onboard", {
        return_url: `${origin}/sitter?connect=return`,
        refresh_url: `${origin}/sitter?connect=refresh`,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(extractError(e)); setBusy(false); }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stripe/connect/login-link");
      window.open(data.url, "_blank");
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="paper-card p-6" data-testid="stripe-connect-card">
        <Loader2 className="w-4 h-4 animate-spin text-sage-600" />
      </div>
    );
  }

  const ready = status?.connected && status?.charges_enabled;

  return (
    <section className="paper-card p-6" data-testid="stripe-connect-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="section-eyebrow">Payouts</span>
          <h2 className="mt-2 font-heading font-semibold text-sage-900 text-2xl flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-sage-600" /> Stripe payout account
          </h2>
          <p className="text-sm text-stone2-600 mt-1 max-w-xl">
            Connect your Stripe account so families' payments are transferred directly to your bank when they book you.
            The platform's 10% service fee is automatically deducted.
          </p>
        </div>
        <div>
          {ready ? (
            <span className="chip"><Check className="w-3.5 h-3.5 mr-1" /> Payouts ready</span>
          ) : status?.connected ? (
            <span className="chip-ochre"><AlertCircle className="w-3.5 h-3.5 mr-1" /> Onboarding incomplete</span>
          ) : (
            <span className="chip-ochre">Not connected</span>
          )}
        </div>
      </div>

      {status?.connected && (
        <ul className="mt-4 grid sm:grid-cols-3 gap-2 text-sm">
          <li className="rounded-lg bg-stone2-50 p-3">
            <div className="text-xs text-stone2-500">Details submitted</div>
            <div className="font-semibold text-sage-900">{status.details_submitted ? "Yes" : "No"}</div>
          </li>
          <li className="rounded-lg bg-stone2-50 p-3">
            <div className="text-xs text-stone2-500">Charges enabled</div>
            <div className="font-semibold text-sage-900">{status.charges_enabled ? "Yes" : "No"}</div>
          </li>
          <li className="rounded-lg bg-stone2-50 p-3">
            <div className="text-xs text-stone2-500">Payouts enabled</div>
            <div className="font-semibold text-sage-900">{status.payouts_enabled ? "Yes" : "No"}</div>
          </li>
        </ul>
      )}

      {status?.requirements?.length > 0 && (
        <div className="mt-3 rounded-lg bg-ochre-50 border border-ochre-200 p-3 text-xs text-ochre-800">
          <div className="font-semibold mb-1">Stripe still needs:</div>
          <ul className="list-disc list-inside">
            {status.requirements.slice(0, 6).map((r) => <li key={r}>{r}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {!ready && (
          <button type="button" onClick={startOnboarding} disabled={busy}
            data-testid="stripe-connect-onboard-btn"
            className="btn-primary text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {status?.connected ? "Continue onboarding" : "Connect with Stripe"}
          </button>
        )}
        {status?.connected && (
          <button type="button" onClick={openDashboard} disabled={busy}
            data-testid="stripe-connect-dashboard-btn"
            className="btn-outline-sage text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            Stripe dashboard
          </button>
        )}
        <button type="button" onClick={refresh} className="btn-secondary text-sm" data-testid="stripe-connect-refresh-btn">
          Refresh status
        </button>
      </div>
    </section>
  );
}
